import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config';
import {
  Calendar,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  RefreshCw,
  AlertCircle,
  XCircle,
  User
} from 'lucide-react';

const Bookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [actionLoading, setActionLoading] = useState(null); // stores bookingId of current patch

  const fetchBookings = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/DemoBooking`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to retrieve demo bookings.');
      }

      const result = await response.json();
      if (result.success) {
        setBookings(result.data);
      }
    } catch (err) {
      console.error(err);
      setError('Could not connect to API. Displaying simulated demo booking records.');
      setBookings(getMockBookings());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateStatus = async (bookingId, newStatus) => {
    setActionLoading(bookingId);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/DemoBooking/${bookingId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update booking status.');
      }

      // Locally update
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
    } catch (err) {
      console.error(err);
      alert('Failed to update booking status. Updating simulated local state instead.');
      setBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status: newStatus } : b));
    } finally {
      setActionLoading(null);
    }
  };

  const getMockBookings = () => {
    return [
      { id: 1, name: "David Miller", email: "david.miller@example.com", gym: "Titan Fitness", message: "We are interested in installing 3 check-in devices. Please call us to discuss pricing.", createdAt: new Date(Date.now() - 3600000 * 4).toISOString(), status: "Pending" },
      { id: 2, name: "Sarah Connor", email: "sarah@cyberdyne.fit", gym: "Resistance Gym", message: "Looking for a walkthrough of the admin dashboard and trainer assignment modules.", createdAt: new Date(Date.now() - 3600000 * 24).toISOString(), status: "Scheduled" },
      { id: 3, name: "Vikram Sen", email: "vikram.sen@fitlife.in", gym: "FitLife India", message: "Interested in the enterprise subscription for multiple franchise gyms.", createdAt: new Date(Date.now() - 3600000 * 72).toISOString(), status: "Completed" },
      { id: 4, name: "James Anderson", email: "james@fastbowling.uk", gym: "", message: "Just testing out the contact form. Please ignore.", createdAt: new Date(Date.now() - 3600000 * 96).toISOString(), status: "Cancelled" }
    ];
  };

  // Filtered bookings
  const filteredBookings = bookings.filter(b => {
    const nameMatch = b.name?.toLowerCase().includes(search.toLowerCase()) || false;
    const emailMatch = b.email?.toLowerCase().includes(search.toLowerCase()) || false;
    const gymMatch = b.gym?.toLowerCase().includes(search.toLowerCase()) || false;
    const msgMatch = b.message?.toLowerCase().includes(search.toLowerCase()) || false;

    const matchesSearch = nameMatch || emailMatch || gymMatch || msgMatch;
    const matchesStatus = statusFilter === 'All' ? true : b.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-500/10 text-green-400 border-green-500/20';
      case 'Scheduled':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'Cancelled':
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      case 'Pending':
      default:
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
    }
  };

  const getStatusDotClass = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-green-400';
      case 'Scheduled':
        return 'bg-blue-400';
      case 'Cancelled':
        return 'bg-red-400';
      case 'Pending':
      default:
        return 'bg-yellow-400';
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-2.5">
            <Calendar className="text-[#6CD800]" size={28} />
            Demo Booking Requests
          </h1>
          <p className="text-gray-400 text-sm mt-1">Manage, schedule, and track product demo bookings from prospects.</p>
        </div>
        <button
          onClick={fetchBookings}
          className="flex items-center px-4 py-2 bg-[#1E293B] border border-[#334155] hover:border-[#6CD800] rounded-lg text-sm text-gray-300 transition-all hover:text-white w-fit"
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm rounded-lg p-4 flex items-center gap-2">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Filter and Search Panel */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl p-5 shadow-lg flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="relative w-full md:max-w-sm">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, gym..."
            className="w-full pl-10 pr-4 py-2 bg-[#0B0F19] border border-[#1E293B] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#6CD800] text-sm transition-colors"
          />
          <span className="absolute left-3 top-2.5 text-gray-500">
            <Search size={18} />
          </span>
        </div>

        <div className="flex flex-wrap gap-4 w-full md:w-auto justify-end">
          <div className="flex items-center space-x-2 bg-[#0B0F19] border border-[#1E293B] px-3 py-2 rounded-lg">
            <Filter size={14} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-transparent border-none text-xs text-gray-300 focus:outline-none cursor-pointer"
            >
              <option value="All">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Scheduled">Scheduled</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Table Grid */}
      <div className="bg-[#0F172A] border border-[#1E293B] rounded-xl shadow-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-48 text-[#6CD800]">
            <RefreshCw size={24} className="animate-spin mr-2" />
            Loading demo bookings...
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-300">
              <thead className="text-xs text-gray-400 uppercase bg-[#0B0F19] border-b border-[#1E293B]">
                <tr>
                  <th scope="col" className="px-6 py-4">Prospect</th>
                  <th scope="col" className="px-6 py-4">Gym / Organization</th>
                  <th scope="col" className="px-6 py-4 w-96">Message</th>
                  <th scope="col" className="px-6 py-4">Request Date</th>
                  <th scope="col" className="px-6 py-4">Status</th>
                  <th scope="col" className="px-6 py-4 text-right">Quick Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E293B]">
                {filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-12 text-gray-500">No demo booking requests found.</td>
                  </tr>
                ) : (
                  filteredBookings.map((booking) => (
                    <tr key={booking.id} className="hover:bg-[#1E293B]/20 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-[#1E293B] border border-[#6CD800]/20 flex items-center justify-center text-[#6CD800]">
                            <User size={16} />
                          </div>
                          <div>
                            <span className="block font-semibold text-white">{booking.name}</span>
                            <span className="block text-xs text-gray-400">{booking.email}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {booking.gym ? (
                          <span className="text-white font-medium">{booking.gym}</span>
                        ) : (
                          <span className="text-gray-500 italic text-xs">Not specified</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
                        {booking.message || <span className="text-gray-500 italic text-xs">No message provided</span>}
                      </td>
                      <td className="px-6 py-4 text-xs text-gray-500">
                        {new Date(booking.createdAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getStatusBadgeClass(booking.status)}`}>
                          <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${getStatusDotClass(booking.status)}`}></span>
                          {booking.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {booking.status === 'Pending' && (
                            <button
                              onClick={() => handleUpdateStatus(booking.id, 'Scheduled')}
                              disabled={actionLoading === booking.id}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 font-bold border border-blue-500/30 rounded-lg text-xs transition-colors disabled:opacity-50"
                            >
                              <Clock size={12} />
                              Schedule
                            </button>
                          )}

                          {(booking.status === 'Pending' || booking.status === 'Scheduled') && (
                            <>
                              <button
                                onClick={() => handleUpdateStatus(booking.id, 'Completed')}
                                disabled={actionLoading === booking.id}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-[#6CD800]/10 hover:bg-[#6CD800]/20 text-[#6CD800] font-bold border border-[#6CD800]/30 rounded-lg text-xs transition-colors disabled:opacity-50"
                              >
                                <CheckCircle2 size={12} />
                                Complete
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(booking.id, 'Cancelled')}
                                disabled={actionLoading === booking.id}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-bold border border-red-500/30 rounded-lg text-xs transition-colors disabled:opacity-50"
                              >
                                <XCircle size={12} />
                                Cancel
                              </button>
                            </>
                          )}

                          {booking.status === 'Completed' && (
                            <span className="text-xs text-green-500 font-semibold flex items-center gap-1">
                              <CheckCircle2 size={12} /> Done
                            </span>
                          )}

                          {booking.status === 'Cancelled' && (
                            <span className="text-xs text-red-500 font-semibold flex items-center gap-1">
                              <XCircle size={12} /> Cancelled
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bookings;
